import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table, Input, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { fetchActivities, clearActivityData } from '../../store/slices/activitySlice';
import useDebounce from '@app/hooks/useDebounce';
import { getDefaultColumns } from './columns';
import { AppDispatch, RootState } from '@app/store/store';
import { ActivityHistoryProps } from '@app/interfaces/interfaces';
import './ActivityHistory.css';
import { showTotalRecords } from '@app/utils/tablePagination';

const ActivityHistory: React.FC<ActivityHistoryProps> = ({
  entityType,
  entityId,
  columns,
  title = 'Activity History',
  extraFilters,
  onRefresh,
  refreshToken,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const cacheKey = `${entityType}-${entityId}`;
  
  const activityData = useSelector((state: RootState) => 
    state.activity.activities[cacheKey] || {
      data: [],
      totalRows: 0,
      loading: false,
      error: null
    }
  );

  const [searchQuery, setSearchQuery] = useState<string>('');
  const debouncedSearch = useDebounce(searchQuery, 500);
  
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const loadData = useCallback(() => {
    dispatch(
      fetchActivities({
        entityType,
        entityId,
        page: pagination.current,
        pageSize: pagination.pageSize,
        searchQuery: debouncedSearch,
      })
    );
  }, [entityType, entityId, pagination.current, pagination.pageSize, debouncedSearch, refreshToken]);

  useEffect(() => {
    loadData();
    return () => {
      dispatch(clearActivityData({ entityType, entityId }));
    };
  }, [loadData]);

  const handleTableChange = (newPagination: any) => {
    setPagination(newPagination);
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setPagination({ ...pagination, current: 1 });
  };

  return (
    <div className="p-3">
      <Space direction="vertical" style={{ width: '100%' }}>
        <Input
          placeholder="Search activities..."
          prefix={<SearchOutlined />}
          onChange={(e) => handleSearch(e.target.value)}
          value={searchQuery}
          className="mb-3"
          size="small"
          allowClear
        />
      </Space>

      <Table
        columns={columns || getDefaultColumns()}
        dataSource={activityData.data}
        loading={activityData.loading}
        pagination={{
          ...pagination,
          total: activityData.totalRows,
          showSizeChanger: true,
          showTotal: showTotalRecords,
        }}
        onChange={handleTableChange}
        //rowClassName="custom-expanded-row"
        style={{backgroundColor: 'white'}}
        rowKey="id"
        size="small"
      />
    </div>
  );
};

export default ActivityHistory;